import { Helmet } from "react-helmet-async";

/** Production site origin (no trailing slash). Override with REACT_APP_SITE_URL at build time. */
export function getSiteUrl() {
  return (process.env.REACT_APP_SITE_URL || "https://www.mansok.com").replace(/\/$/, "");
}

const DEFAULT_DESCRIPTION =
  "MANSOK DIGITAL Solutions engineers websites, automation, and AI systems that eliminate inefficiency and increase profit.";

const ROUTE_META = {
  "/": {
    title: "MANSOK DIGITAL Solutions | Revenue-Driving Technology",
    description: DEFAULT_DESCRIPTION
  },
  "/services": {
    title: "Services | MANSOK DIGITAL Solutions",
    description:
      "Growth infrastructure, automation and AI systems, and custom business software — sprint-based delivery focused on revenue and response speed."
  },
  "/systems": {
    title: "Business Systems | MANSOK DIGITAL Solutions",
    description:
      "Connected systems for lead capture, qualification, CRM, automation, and reporting — designed for daily execution, not shelf-ware."
  },
  "/audit": {
    title: "Free System Audit | MANSOK DIGITAL Solutions",
    description:
      "Free system audit: bottleneck diagnosis, impact-prioritized roadmap, architecture recommendations, and a practical 30–90 day action plan."
  },
  "/contact": {
    title: "Contact | MANSOK DIGITAL Solutions",
    description:
      "Contact MANSOK DIGITAL Solutions. Share your goals and receive a direct plan with timeline, system priorities, and delivery approach."
  }
};

function normalizePath(pathname) {
  if (!pathname || pathname === "/") return "/";
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed || "/";
}

export function SeoHead({ pathname }) {
  const path = normalizePath(pathname);
  const meta = ROUTE_META[path] || ROUTE_META["/"];
  const siteUrl = getSiteUrl();
  const canonical = path === "/" ? `${siteUrl}/` : `${siteUrl}${path}`;
  const ogImage = `${siteUrl}${encodeURI("/mansok logo.jpg")}`;

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "MANSOK DIGITAL Solutions",
    url: `${siteUrl}/`,
    image: ogImage,
    logo: ogImage,
    email: "info@mansok.com",
    telephone: "+252614375195",
    description: DEFAULT_DESCRIPTION,
    address: {
      "@type": "PostalAddress",
      addressCountry: "SO"
    }
  };

  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "MANSOK DIGITAL Solutions",
    url: `${siteUrl}/`,
    publisher: { "@type": "Organization", name: "MANSOK DIGITAL Solutions", url: `${siteUrl}/` }
  };

  return (
    <Helmet htmlAttributes={{ lang: "en" }} prioritizeSeoTags>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="theme-color" content="#121c35" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="MANSOK DIGITAL Solutions" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={ogImage} />
      <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
    </Helmet>
  );
}
